// The survey page's Placements section (redesign spec §7): one
// PlacementSection card per placement, by name, plus the draft "New
// placement" while one is being added.

import { Button, type SectionNavItem } from "@pej/kore/components";
import { Plus } from "lucide-react";
import { usePlacements } from "@/api/placements";
import type { PlacementRow } from "@/api/types";
import { PageError, useRetry } from "@/components/page-states";
import { useSectionEditController } from "@/components/section-page/section-edit";
import { SECTION } from "@/features/surveys/survey-sections";
import { PlacementSection } from "./placement-section";

export interface PlacementsSectionProps {
    workspaceId: string;
    surveyId: string;
    draft: boolean;
    onDraft: (open: boolean) => void;
}

function inPageOrder(placements: PlacementRow[]): PlacementRow[] {
    return [...placements].sort((a, b) => a.name.localeCompare(b.name));
}

export function PlacementsSection({ workspaceId, surveyId, draft, onDraft }: PlacementsSectionProps) {
    const controller = useSectionEditController();
    const placementsQuery = usePlacements(surveyId);
    const placementsRetry = useRetry(placementsQuery);
    const placements = inPageOrder(placementsQuery.data ?? []);
    const addBlocked = controller.isAnySectionActive || draft;

    return (
        <section
            id={SECTION.placements}
            aria-labelledby={`${SECTION.placements}-title`}
            className="flex scroll-mt-[4.5rem] flex-col gap-6"
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 id={`${SECTION.placements}-title`} className="text-lg font-semibold">
                        Placements
                    </h2>
                    <p className="mt-1 text-sm text-kore-text-muted">
                        Where guests answer this survey. Each saves on its own.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={addBlocked}
                    title={addBlocked ? "Save or cancel the current edit first." : undefined}
                    onClick={() => onDraft(true)}
                >
                    <Plus aria-hidden="true" />
                    Add placement
                </Button>
            </div>

            {placementsRetry.failed && <PageError level={3} {...placementsRetry.props} />}
            {placements.map((placement) => (
                <PlacementSection
                    key={placement.id}
                    workspaceId={workspaceId}
                    surveyId={surveyId}
                    placement={placement}
                />
            ))}
            {draft && (
                <PlacementSection
                    workspaceId={workspaceId}
                    surveyId={surveyId}
                    placement={null}
                    onDraftDone={() => onDraft(false)}
                />
            )}
        </section>
    );
}

export function placementNavItems(
    placements: PlacementRow[],
    draft: boolean,
    errors: Record<string, boolean>,
): SectionNavItem[] {
    const children: SectionNavItem[] = inPageOrder(placements).map((placement) => ({
        id: SECTION.placement(placement.id),
        label: placement.name,
        hasError: errors[SECTION.placement(placement.id)],
    }));
    if (draft)
        children.push({ id: SECTION.newPlacement, label: "New placement", hasError: errors[SECTION.newPlacement] });
    return [{ id: SECTION.placements, label: "Placements", children }];
}
